import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LoyaltyPage from "@/pages/LoyaltyPage";
import {
  Plus, PencilSimple, Trash, User, MagnifyingGlass,
  Phone, Envelope, Clock, Receipt, Star, ShoppingCart
} from "@phosphor-icons/react";

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "", enroll_loyalty: false });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [addingLoyalty, setAddingLoyalty] = useState(false);

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customers`);
      setCustomers(res.data);
    } catch (err) { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await axios.post(`${API}/customers`, { name: form.name, phone: form.phone, email: form.email, notes: form.notes });
        toast.success("Customer added");
        // Optionally enroll in loyalty program
        if (form.enroll_loyalty && form.email.trim()) {
          try {
            await axios.post(`${API}/loyalty/customers`, {
              name: form.name,
              email: form.email,
              phone: form.phone || "",
            });
            toast.success("Also enrolled in loyalty program");
          } catch (e) {
            const d = e.response?.data?.detail || "";
            if (d.toLowerCase().includes("already exists")) {
              toast.info("Already a loyalty member — skipped");
            } else {
              toast.warning("Customer added, but loyalty enrollment failed");
            }
          }
        }
      }
      setShowDialog(false);
      setEditing(null);
      setForm({ name: "", phone: "", email: "", notes: "", enroll_loyalty: false });
      fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, phone: c.phone || "", email: c.email || "", notes: c.notes || "", enroll_loyalty: false });
    setShowDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/customers/${deleteConfirm.id}`);
      toast.success("Customer deleted");
      setDeleteConfirm(null);
      if (selectedCustomer?.id === deleteConfirm.id) setSelectedCustomer(null);
      fetchCustomers();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const handleAdd = () => {
    setEditing(null);
    setForm({ name: "", phone: "", email: "", notes: "", enroll_loyalty: false });
    setShowDialog(true);
  };

  const viewCustomer = async (c) => {
    try {
      const res = await axios.get(`${API}/customers/${c.id}`);
      setSelectedCustomer(res.data);
    } catch (err) { toast.error("Failed to load customer details"); }
  };

  const handleAddToLoyalty = async () => {
    if (!selectedCustomer) return;
    if (!selectedCustomer.email) {
      toast.error("Customer needs an email address to join the loyalty program");
      return;
    }
    setAddingLoyalty(true);
    try {
      await axios.post(`${API}/loyalty/customers`, {
        name: selectedCustomer.name,
        email: selectedCustomer.email,
        phone: selectedCustomer.phone || "",
      });
      toast.success(`${selectedCustomer.name} added to the loyalty program!`);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.toLowerCase().includes("already exists")) {
        toast.info("This customer is already a loyalty member");
      } else {
        toast.error(detail || "Failed to add to loyalty program");
      }
    } finally {
      setAddingLoyalty(false);
    }
  };

  const filtered = customers.filter((c) =>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || "").includes(search) ||
    (c.email || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background" data-testid="customers-page">
      <Sidebar />
      <main className="main-content p-6">
        <Tabs defaultValue="customers" className="space-y-4">
          <TabsList data-testid="customers-tabs">
            <TabsTrigger value="customers" className="gap-2" data-testid="tab-customers"><User size={16} /> Customers</TabsTrigger>
            <TabsTrigger value="loyalty" className="gap-2" data-testid="tab-loyalty"><Star size={16} /> Loyalty</TabsTrigger>
          </TabsList>

          <TabsContent value="customers" className="space-y-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-medium">Customers</h1>
            <p className="text-muted-foreground">Customer database and purchase history</p>
          </div>
          <Button className="gap-2" onClick={handleAdd} data-testid="add-customer-btn">
            <Plus size={18} /> Add Customer
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center"><User size={20} className="text-primary" weight="fill" /></div><div><p className="text-xs text-muted-foreground">Total Customers</p><p className="font-mono text-xl font-medium" data-testid="customer-total">{customers.length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center"><ShoppingCart size={20} className="text-green-600" weight="fill" /></div><div><p className="text-xs text-muted-foreground">Total Orders</p><p className="font-mono text-xl font-medium">{customers.reduce((s, c) => s + (c.total_orders || 0), 0)}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"><Receipt size={20} className="text-blue-600" weight="fill" /></div><div><p className="text-xs text-muted-foreground">Total Revenue</p><p className="font-mono text-xl font-medium">${customers.reduce((s, c) => s + (c.total_spent || 0), 0).toFixed(0)}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center"><Star size={20} className="text-orange-500" weight="fill" /></div><div><p className="text-xs text-muted-foreground">Avg per Customer</p><p className="font-mono text-xl font-medium">${customers.length ? (customers.reduce((s, c) => s + (c.total_spent || 0), 0) / customers.length).toFixed(0) : 0}</p></div></CardContent></Card>
        </div>

        {/* Search */}
        <div className="flex justify-end mb-4">
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search by name, phone, or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="customer-search" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer List */}
          <div className={selectedCustomer ? "lg:col-span-1" : "lg:col-span-3"}>
            {loading ? (
              <div className="flex items-center justify-center py-20"><div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground"><User size={48} className="mx-auto mb-2 opacity-30" /><p>No customers found</p><Button onClick={handleAdd} className="mt-3 gap-2"><Plus size={16} /> Add Customer</Button></CardContent></Card>
            ) : (
              <div className="space-y-2">
                {filtered.map(c => (
                  <Card key={c.id} className={`cursor-pointer hover:shadow-sm transition-shadow ${selectedCustomer?.id === c.id ? "ring-2 ring-primary" : ""}`} onClick={() => viewCustomer(c)} data-testid={`customer-${c.id}`}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                        <User size={20} className="text-primary" weight="bold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{c.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {c.phone && <span className="flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
                          {c.email && <span className="flex items-center gap-1 truncate"><Envelope size={10} />{c.email}</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-mono text-sm font-medium">${(c.total_spent || 0).toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">{c.total_orders || 0} orders</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Customer Detail Panel */}
          {selectedCustomer && (
            <div className="lg:col-span-2 space-y-4" data-testid="customer-detail">
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                        <User size={28} className="text-primary" weight="bold" />
                      </div>
                      <div>
                        <CardTitle className="text-xl" data-testid="detail-customer-name">{selectedCustomer.name}</CardTitle>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          {selectedCustomer.phone && <span className="flex items-center gap-1"><Phone size={14} />{selectedCustomer.phone}</span>}
                          {selectedCustomer.email && <span className="flex items-center gap-1"><Envelope size={14} />{selectedCustomer.email}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleEdit(selectedCustomer); }} data-testid="edit-customer-btn"><PencilSimple size={18} /></Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(selectedCustomer); }} data-testid="delete-customer-btn"><Trash size={18} /></Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-secondary/50 rounded-xl p-3 text-center">
                      <p className="text-xs text-muted-foreground">Total Spent</p>
                      <p className="font-mono text-lg font-medium">${(selectedCustomer.total_spent || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-secondary/50 rounded-xl p-3 text-center">
                      <p className="text-xs text-muted-foreground">Orders</p>
                      <p className="font-mono text-lg font-medium">{selectedCustomer.total_orders || 0}</p>
                    </div>
                    <div className="bg-secondary/50 rounded-xl p-3 text-center">
                      <p className="text-xs text-muted-foreground">Last Visit</p>
                      <p className="text-sm font-medium">{selectedCustomer.last_order_date ? new Date(selectedCustomer.last_order_date).toLocaleDateString() : "—"}</p>
                    </div>
                  </div>
                  {selectedCustomer.notes && <p className="text-sm text-muted-foreground italic mb-3">{selectedCustomer.notes}</p>}
                  <div className="flex justify-end pt-2">
                    <Button
                      size="sm"
                      className="gap-2"
                      onClick={handleAddToLoyalty}
                      disabled={addingLoyalty}
                      data-testid="add-to-loyalty-btn"
                    >
                      <Star size={14} weight="fill" />
                      {addingLoyalty ? "Adding..." : "Add to Loyalty Program"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Frequent Items */}
              {selectedCustomer.top_items?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Most Ordered Items</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {selectedCustomer.top_items.map((item, i) => (
                        <Badge key={i} variant="outline" className="text-sm py-1.5 px-3 gap-1.5" data-testid={`freq-item-${i}`}>
                          {item.name} <span className="font-mono text-primary">x{item.count}</span>
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Last Order */}
              {selectedCustomer.last_order_items?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Last Order</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {selectedCustomer.last_order_items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm py-1 border-b border-dashed border-border/50 last:border-0">
                          <span><span className="font-mono text-muted-foreground mr-2">{item.quantity}x</span>{item.name}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Purchase History */}
              {selectedCustomer.purchase_history?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Purchase History</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedCustomer.purchase_history.map((order) => (
                        <div key={order.order_id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg" data-testid={`order-history-${order.order_id}`}>
                          <div>
                            <p className="text-sm font-medium">{order.items.map(i => `${i.quantity}x ${i.name}`).join(", ")}</p>
                            <p className="text-xs text-muted-foreground">{order.date ? new Date(order.date).toLocaleDateString() : ""}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-mono text-sm font-medium">${order.total.toFixed(2)}</p>
                            <Badge variant="outline" className="text-xs capitalize">{order.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="loyalty">
            <LoyaltyPage embedded />
          </TabsContent>
        </Tabs>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={(o) => { if (!o) { setShowDialog(false); setEditing(null); } }}>
          <DialogContent data-testid="customer-dialog">
            <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Edit Customer" : "Add Customer"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" data-testid="customer-name" /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" data-testid="customer-phone" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@email.com" data-testid="customer-email" /></div>
              <div className="space-y-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Allergies, preferences..." data-testid="customer-notes" /></div>
              {!editing && (
                <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-border bg-secondary/30 px-3 py-2.5 hover:bg-secondary/50 transition-colors" data-testid="enroll-loyalty-row">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-primary"
                    checked={form.enroll_loyalty}
                    onChange={(e) => setForm({ ...form, enroll_loyalty: e.target.checked })}
                    disabled={!form.email.trim()}
                    data-testid="enroll-loyalty-checkbox"
                  />
                  <div className="text-sm">
                    <div className="flex items-center gap-1.5 font-medium">
                      <Star size={14} weight="fill" className="text-amber-500" />
                      Add to Loyalty Program
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {form.email.trim() ? "Earns 1 point per $1 spent, starting at Bronze tier." : "Add an email above to enable loyalty enrollment."}
                    </p>
                  </div>
                </label>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDialog(false); setEditing(null); }}>Cancel</Button>
              <Button onClick={handleSave} data-testid="save-customer-btn">{editing ? "Save" : "Add Customer"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="sm:max-w-[400px]" data-testid="delete-customer-confirm">
            <DialogHeader><DialogTitle className="font-heading text-xl">Delete Customer</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground py-2">Are you sure you want to delete <span className="font-medium text-foreground">{deleteConfirm?.name}</span>? This action cannot be undone.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-customer-btn">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CustomersPage;
